import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import LandingPage from '@/pages/LandingPage';
import TeacherLogin from '@/pages/TeacherLogin';
import StudentLogin from '@/pages/StudentLogin';
import HeadTeacherLogin from '@/pages/HeadTeacherLogin';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import ParentLogin from '@/pages/ParentLogin';
import SuperAdminLogin from '@/pages/SuperAdminLogin';
import TeacherDashboard from '@/pages/teacher/NewTeacherDashboard';
import StudentDashboard from '@/pages/student/NewStudentDashboard';
import HeadTeacherDashboard from '@/pages/headteacher/HeadTeacherDashboard';
import ParentDashboard from '@/pages/parent/ParentDashboard';
import SuperAdminDashboard from '@/pages/superadmin/SuperAdminDashboard';
import DocsPage from '@/pages/DocsPage';
import { Toaster } from '@/components/ui/sonner';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && user.role !== requiredRole) {
    // Redirect based on role
    const redirectMap = {
      'teacher': '/teacher/dashboard',
      'student': '/student/dashboard',
      'head_teacher': '/head-teacher/dashboard',
      'school_admin': '/admin/dashboard',
      'parent': '/parent/dashboard'
    };
    return <Navigate to={redirectMap[user.role] || '/'} />;
  }

  return children;
};

// Helper to get dashboard route based on user role
const getDashboardRoute = (role) => {
  const routes = {
    'teacher': '/teacher/dashboard',
    'student': '/student/dashboard',
    'head_teacher': '/head-teacher/dashboard',
    'school_admin': '/admin/dashboard',
    'parent': '/parent/dashboard'
  };
  return routes[role] || '/';
};

function AppContent() {
  const { user } = useAuth();

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={user ? <Navigate to={getDashboardRoute(user.role)} /> : <LandingPage />} />
        <Route path="/teacher-login" element={user ? <Navigate to={getDashboardRoute(user.role)} /> : <TeacherLogin />} />
        <Route path="/student-login" element={user ? <Navigate to={getDashboardRoute(user.role)} /> : <StudentLogin />} />
        <Route path="/head-teacher-login" element={user ? <Navigate to={getDashboardRoute(user.role)} /> : <HeadTeacherLogin />} />
        <Route path="/head-teacher/login" element={user ? <Navigate to={getDashboardRoute(user.role)} /> : <HeadTeacherLogin />} />
        <Route path="/admin/login" element={user ? <Navigate to={getDashboardRoute(user.role)} /> : <AdminLogin />} />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute requiredRole="school_admin">
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={<Navigate to="/admin/login" />} />
        <Route path="/parent-login" element={user ? <Navigate to={getDashboardRoute(user.role)} /> : <ParentLogin />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/docs/business" element={<DocsPage />} />
        <Route path="/docs/launch-plan" element={<DocsPage />} />
        <Route path="/docs/tech" element={<DocsPage />} />
        <Route path="/docs/brd" element={<DocsPage />} />
        <Route path="/docs/blueprint" element={<DocsPage />} />
        <Route path="/login" element={<Navigate to="/" />} />
        
        {/* Teacher Routes */}
        <Route
          path="/teacher/*"
          element={
            <ProtectedRoute requiredRole="teacher">
              <TeacherDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Student Routes */}
        <Route
          path="/student/*"
          element={
            <ProtectedRoute requiredRole="student">
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Head Teacher Routes */}
        <Route
          path="/head-teacher/*"
          element={
            <ProtectedRoute requiredRole="head_teacher">
              <HeadTeacherDashboard />
            </ProtectedRoute>
          }
        />

        {/* Parent Routes */}
        <Route
          path="/parent/*"
          element={
            <ProtectedRoute requiredRole="parent">
              <ParentDashboard />
            </ProtectedRoute>
          }
        />

        {/* Super Admin Routes - Separate auth system */}
        <Route path="/super-admin/login" element={<SuperAdminLogin />} />
        <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
        <Route path="/super-admin" element={<Navigate to="/super-admin/login" />} />
      </Routes>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;